/**
 * Generates a 1080×1080 branded Instagram-ready share card composed on an HTML
 * canvas. No backend / LLM call — everything happens in the browser in ~50ms.
 *
 * Layout:
 *   ┌─────────────────────────────────────┐  1080×1080
 *   │  cream backdrop + subtle grain      │
 *   │                                     │
 *   │      [ 620×620 rounded card ]       │
 *   │      │                       │      │
 *   │      │      user logo        │      │
 *   │      │                       │      │
 *   │      └───────────────────────┘      │
 *   │                                     │
 *   │    Meet {brandName}.                │
 *   │    Our brand-new logo, made with AI.│
 *   │                                     │
 *   │    ─────────────────                │
 *   │    talaio.com/logo-studio           │
 *   │    Free AI Logo Generator           │
 *   └─────────────────────────────────────┘
 */
export async function generateShareCard({ logoBase64, brandName }) {
  const SIZE = 1080;
  const canvas = document.createElement("canvas");
  canvas.width = SIZE;
  canvas.height = SIZE;
  const ctx = canvas.getContext("2d");

  // 1) Warm cream backdrop (matches Talaio palette)
  const bgGrad = ctx.createLinearGradient(0, 0, SIZE, SIZE);
  bgGrad.addColorStop(0, "#FAF7F1");
  bgGrad.addColorStop(1, "#F3ECE0");
  ctx.fillStyle = bgGrad;
  ctx.fillRect(0, 0, SIZE, SIZE);

  // Subtle grain (small pink dots)
  ctx.fillStyle = "rgba(219, 39, 119, 0.04)";
  for (let i = 0; i < 220; i++) {
    const x = Math.random() * SIZE;
    const y = Math.random() * SIZE;
    ctx.beginPath();
    ctx.arc(x, y, 1.2, 0, Math.PI * 2);
    ctx.fill();
  }

  // 2) CMYK color-bar strip at the very top (Talaio signature)
  const bandH = 12;
  const bandColors = ["#00AEEF", "#EC008C", "#FFF200", "#000000"];
  bandColors.forEach((c, i) => {
    ctx.fillStyle = c;
    ctx.fillRect((SIZE / 4) * i, 0, SIZE / 4, bandH);
  });

  // 3) Logo card — 620×620 rounded white card, centred, subtle shadow
  const cardSize = 620;
  const cardX = (SIZE - cardSize) / 2;
  const cardY = 130;
  const radius = 32;

  ctx.save();
  ctx.shadowColor = "rgba(0,0,0,0.10)";
  ctx.shadowBlur = 40;
  ctx.shadowOffsetY = 12;
  ctx.fillStyle = "#FFFFFF";
  roundedRect(ctx, cardX, cardY, cardSize, cardSize, radius);
  ctx.fill();
  ctx.restore();

  // 4) Draw the user's logo centred inside the card (with 12% padding)
  await new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const pad = cardSize * 0.12;
      const box = cardSize - pad * 2;
      const scale = Math.min(box / img.width, box / img.height);
      const drawW = img.width * scale;
      const drawH = img.height * scale;
      const dx = cardX + (cardSize - drawW) / 2;
      const dy = cardY + (cardSize - drawH) / 2;
      // Clip to rounded card so any near-transparent PNG edges look tidy
      ctx.save();
      roundedRect(ctx, cardX, cardY, cardSize, cardSize, radius);
      ctx.clip();
      ctx.drawImage(img, dx, dy, drawW, drawH);
      ctx.restore();
      resolve();
    };
    img.onerror = reject;
    img.src = `data:image/png;base64,${logoBase64}`;
  });

  // 5) Brand-name heading
  const textStartY = cardY + cardSize + 90;
  ctx.fillStyle = "#0A0A0A";
  ctx.textAlign = "center";
  ctx.font = "900 68px Inter, 'Helvetica Neue', Arial, sans-serif";
  const trimmedName = brandName && brandName.trim() ? brandName.trim() : "our new brand";
  ctx.fillText(fitText(ctx, `Meet ${trimmedName}.`, SIZE - 160), SIZE / 2, textStartY);

  // 6) Subline
  ctx.fillStyle = "#525252";
  ctx.font = "500 30px Inter, 'Helvetica Neue', Arial, sans-serif";
  ctx.fillText("Our brand-new logo — made with AI in 60 seconds.", SIZE / 2, textStartY + 54);

  // 7) Small pink divider
  ctx.fillStyle = "#DB2777";
  ctx.fillRect(SIZE / 2 - 40, textStartY + 92, 80, 4);

  // 8) Talaio footer
  ctx.fillStyle = "#0A0A0A";
  ctx.font = "900 34px Inter, 'Helvetica Neue', Arial, sans-serif";
  ctx.fillText("talaio.com/logo-studio", SIZE / 2, textStartY + 148);

  ctx.fillStyle = "#525252";
  ctx.font = "500 22px Inter, 'Helvetica Neue', Arial, sans-serif";
  ctx.fillText("Free AI Logo Generator for Indian small businesses", SIZE / 2, textStartY + 184);

  return canvas.toDataURL("image/png");
}

function roundedRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y,     x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x,     y + h, r);
  ctx.arcTo(x,     y + h, x,     y,     r);
  ctx.arcTo(x,     y,     x + w, y,     r);
  ctx.closePath();
}

// Shrink text if it doesn't fit the given width (rough guard, not a full auto-resize)
function fitText(ctx, text, maxWidth) {
  let t = text;
  while (ctx.measureText(t).width > maxWidth && t.length > 4) {
    const original = ctx.font;
    // shrink by 4px per iteration
    const m = original.match(/(\d+)px/);
    if (!m) break;
    const size = parseInt(m[1], 10);
    if (size < 34) break;
    ctx.font = original.replace(/\d+px/, `${size - 4}px`);
  }
  return t;
}

/**
 * Triggers a download of the generated share card as a PNG file.
 */
export function downloadShareCard(dataUrl, filename) {
  const a = document.createElement("a");
  a.href = dataUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

/**
 * Uses the Web Share API if the browser supports it (mostly mobile / Instagram).
 * Falls back to plain download otherwise.
 */
export async function shareOrDownloadCard(dataUrl, filename, shareText) {
  try {
    const res = await fetch(dataUrl);
    const blob = await res.blob();
    const file = new File([blob], filename, { type: "image/png" });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({
        title: "My new logo — Talaio/AI",
        text: shareText,
        files: [file],
      });
      return "shared";
    }
  } catch (_) { /* fall through */ }
  downloadShareCard(dataUrl, filename);
  return "downloaded";
}
